import * as React from "react";

import { useDispatch } from "react-redux";

import {
  UIFieldMap,
  useMerchantModel,
  useSaveFormCompletion,
  useUiFieldMap,
} from "client/hooks";
import { MerchantFieldMapper, SalesChannel } from "client/lib/apt-merchant";
import { errorActions } from "client/store";

import { FormBuilder } from "../FormBuilder";
import { YesOrNoQuestionsHeader } from "../YesOrNoQuestionsHeader";

import { StoreLocationsForm } from "./StoreLocationsForm";

export type StoreDetailsFieldUiMap = MerchantFieldMapper<UIFieldMap>;

// field order reflects input order
export const storeDetailsUiFieldMap: StoreDetailsFieldUiMap = {
  storeDetails: {
    storeName: { width: 12 },
    salesChannel: { width: 12 },
    contactlessPayment: { width: 12 },
    website: { width: 12 },
    facebookUrl: {},
    platform: {},
    numOfStores: {},
    acquirer: {},
    totalAnnualSales: { width: 12 },
    totalAnnualSalesInStore: { width: 12 },
    industry: { width: 12 },
    subIndustry: { width: 12 },
    selfFulfillment: { width: 12 },
    aveTimeToShip: { width: 12 },
    interestedInXBT: { width: 12 },
    currenciesAvailable: { width: 12 },
    // YES/NO risk-related radio questions
    preOrders: { width: 12, customFieldHeader: <YesOrNoQuestionsHeader /> },
    // Australia only
    websiteNotLive: { width: 12 },
    ddsRestricted: { width: 12 },
    // Canada / Quebec / Sole Prop field only
    artisan: { width: 12 },
    // Canada / Quebec / Sole Prop field only
    nonFamilyEmployee: { width: 12 },
    // UK / footwear reseller field only
    reseller: { width: 12 },
    multiTerminal: { width: 12 },
  },
};

export const StoreDetailsForm: React.FC = () => {
  const {
    formatMerchantForSave,
    formatMerchantForStoreInfo,
    merchant,
    removeFieldSetFromCollection,
    validateMerchant,
  } = useMerchantModel();
  const dispatch = useDispatch();
  if (storeDetailsUiFieldMap.storeDetails?.preOrders) {
    storeDetailsUiFieldMap.storeDetails.preOrders.customFieldHeader = (
      <YesOrNoQuestionsHeader />
    );
  }

  const { mutate: completeStoreDetails } = useSaveFormCompletion(dispatch);

  const { orderedUiFieldMap } = useUiFieldMap(storeDetailsUiFieldMap);

  /**
   * Could go with Experiment here, but that seems best suited for multi-variant.
   * Worth rethinking?
   */
  const shouldShowStoreLocations =
    merchant.storeDetails?.salesChannel?.value &&
    (["Online and In-store", "In-store Only"] as SalesChannel[]).includes(
      merchant.storeDetails?.salesChannel?.value
    );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    /**
     * If the merchant is applying for in-store or online / in-store
     * We need to validate that they have at least one merchant
     * -- or have submitted a spreadsheet
     *
     * Currently store locations are validated whenever we add / edit a new one
     * but this covers the edge case where a user just fills out the first form
     */
    if (shouldShowStoreLocations) {
      if (merchant.locations.some((location) => !validateMerchant(location))) {
        dispatch(
          errorActions.setError(
            "You have missing or invalid store location fields"
          )
        );
        return;
      }
    } else {
      merchant.locations.map((_, i) =>
        removeFieldSetFromCollection("locations", i)
      );
    }
    await completeStoreDetails({
      saveData: formatMerchantForSave(),
      formCompletionData: {
        storeDetailsForm: true,
      },
      storeData: formatMerchantForStoreInfo(),
    });
  };

  return (
    <FormBuilder
      fieldSet={merchant.storeDetails}
      footer={shouldShowStoreLocations ? <StoreLocationsForm /> : null}
      onSubmit={handleSubmit}
      uiFieldMap={orderedUiFieldMap.storeDetails}
    />
  );
};
